/**
 * Client-side attribution pixel
 *
 * Parses UTM + click IDs on page load.
 * Sets signed first-party cookie `l21_attr` (HMAC verified server-side, 365d TTL).
 * Fires browser pixel POST to `/v1/t/<event>` with generated `event_id` (ULID).
 *
 * Events: pv, prescreen_start, prescreen_complete, consent
 * Consent gating: no PII exits browser before registry consent.
 */

import { API_BASE } from "./api";

const COOKIE_NAME = "l21_attr";
const COOKIE_TTL_DAYS = 365;

interface UtmPayload {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  term?: string;
}

interface TrackingPayload {
  event_id: string;
  trial_id: string;
  utm?: UtmPayload;
  fbclid?: string;
  gclid?: string;
  msclkid?: string;
  session_id?: string;
  user_hash?: string;
  occurred_at?: string;
  email?: string;
  phone?: string;
  qualified?: boolean;
  consent_given?: boolean;
  consent_version?: string;
}

/**
 * Minimal Crockford base32 ULID generator (no external dep).
 */
function ulid(): string {
  const ENCODING = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
  const now = Date.now();
  let ts = "";
  let n = now;
  for (let i = 0; i < 10; i++) {
    ts = ENCODING[n % 32] + ts;
    n = Math.floor(n / 32);
  }
  let rand = "";
  for (let i = 0; i < 16; i++) {
    rand += ENCODING[Math.floor(Math.random() * 32)];
  }
  return ts + rand;
}

function parseUtmFromUrl(): UtmPayload {
  const params = new URLSearchParams(window.location.search);
  const utm: UtmPayload = {};
  if (params.has("utm_source")) utm.source = params.get("utm_source") || undefined;
  if (params.has("utm_medium")) utm.medium = params.get("utm_medium") || undefined;
  if (params.has("utm_campaign")) utm.campaign = params.get("utm_campaign") || undefined;
  if (params.has("utm_content")) utm.content = params.get("utm_content") || undefined;
  if (params.has("utm_term")) utm.term = params.get("utm_term") || undefined;
  return utm;
}

function parseClickIds(): { fbclid?: string; gclid?: string; msclkid?: string } {
  const params = new URLSearchParams(window.location.search);
  return {
    fbclid: params.get("fbclid") || undefined,
    gclid: params.get("gclid") || undefined,
    msclkid: params.get("msclkid") || undefined,
  };
}

// document.cookie throws SecurityError in sandboxed iframes and opaque
// origins; attribution then degrades to a no-op instead of breaking the page.
function getCookie(name: string): string | undefined {
  let cookie: string;
  try {
    cookie = document.cookie;
  } catch {
    return undefined;
  }
  const match = cookie.match(new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : undefined;
}

function setCookie(name: string, value: string, days: number): void {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  try {
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax${window.location.protocol === "https:" ? "; Secure" : ""}`;
  } catch {
    // Cookies unavailable; skip the attribution cookie.
  }
}

// Used when sessionStorage throws (Safari private mode, blocked site data,
// quota): one id for this page load, so events still share a session.
let memorySessionId: string | undefined;

function getSessionId(): string {
  try {
    const sid = sessionStorage.getItem("l21_session_id");
    if (sid) return sid;
  } catch {
    // Storage unavailable; fall back to the in-memory id.
  }
  memorySessionId ??= ulid();
  try {
    sessionStorage.setItem("l21_session_id", memorySessionId);
  } catch {
    // Storage unavailable; the in-memory id covers this page load.
  }
  return memorySessionId;
}

/**
 * Initialize attribution on page load.
 * Call once per page (e.g. in root layout or app entry).
 */
export function initAttribution(trialId: string): void {
  const utm = parseUtmFromUrl();
  const clickIds = parseClickIds();

  // Set / refresh first-party cookie with attribution data
  const attrData = JSON.stringify({
    utm,
    ...clickIds,
    set_at: new Date().toISOString(),
  });
  setCookie(COOKIE_NAME, attrData, COOKIE_TTL_DAYS);

  // Fire pageview
  fireEvent("pv", trialId);
}

/**
 * Fire a tracking event.
 * @param eventType One of: pv, prescreen_start, prescreen_complete, consent
 * @param trialId Trial identifier (e.g. vesper5-us)
 * @param extra Additional payload fields (email, phone, qualified, consent_given)
 */
export function fireEvent(
  eventType: "pv" | "prescreen_start" | "prescreen_complete" | "consent",
  trialId: string,
  extra?: Omit<TrackingPayload, "event_id" | "trial_id" | "utm" | "fbclid" | "gclid" | "msclkid" | "session_id" | "occurred_at">,
  intakeToken?: string | null
): void {
  const eventId = ulid();
  const occurredAt = new Date().toISOString();

  const attrCookie = getCookie(COOKIE_NAME);
  let attr: Record<string, unknown> = {};
  try {
    if (attrCookie) attr = JSON.parse(attrCookie);
  } catch {
    // ignore malformed cookie
  }

  const payload: TrackingPayload = {
    event_id: eventId,
    trial_id: trialId,
    utm: (attr.utm as UtmPayload) || undefined,
    fbclid: (attr.fbclid as string) || undefined,
    gclid: (attr.gclid as string) || undefined,
    msclkid: (attr.msclkid as string) || undefined,
    session_id: getSessionId(),
    occurred_at: occurredAt,
    ...extra,
  };

  // Consent gating: strip PII if consent not given
  if (!payload.consent_given) {
    delete payload.email;
    delete payload.phone;
  }

  const url = `${API_BASE}/v1/t/${eventType}`;

  // sendBeacon cannot attach Authorization. Use it only for anonymous
  // pageviews; authenticated funnel events use fetch with the intake JWT.
  if (!intakeToken && navigator.sendBeacon) {
    navigator.sendBeacon(url, JSON.stringify(payload));
  } else {
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(intakeToken ? { Authorization: `Bearer ${intakeToken}` } : {}),
      },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {
      // Silently fail — pixel should not break user experience
    });
  }
}

/**
 * Hash email locally (optional pre-hash before sending).
 * Returns hex SHA-256. Use only after consent is recorded.
 */
export async function hashEmail(email: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(email.toLowerCase().trim());
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}
